import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { LOGO_URL, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function daysInMonth(m) {
  // m is 1-12; use 29 for Feb so leap-year birthdays work
  return [0, 31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m] || 31;
}

export default function SignupPage() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/";
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [bMonth, setBMonth] = useState("");
  const [bDay, setBDay] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!bMonth || !bDay) { toast.error("Please select your birthday month and day"); return; }
    const birthday = `${String(bMonth).padStart(2, "0")}-${String(bDay).padStart(2, "0")}`;
    setSubmitting(true);
    try {
      await signup(fullName, "", password, phone, email, birthday, referralCode.trim());
      toast.success("Account created!");
      nav(redirect);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSubmitting(false); }
  };

  const dayCount = bMonth ? daysInMonth(parseInt(bMonth, 10)) : 31;

  return (
    <Layout>
      <section className="page-section min-h-[80vh] flex items-center" data-testid="signup-page">
        <div className="max-w-md w-full mx-auto px-4">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Sugary Sweet" className="h-20 mx-auto mb-3 logo-white drop-shadow-lg" />
            <h1 className="font-display font-extrabold text-3xl md:text-4xl text-white drop-shadow-lg">Create Account</h1>
            <p className="text-white/90 mt-2 text-sm">Just the essentials — you'll be ordering in seconds.</p>
          </div>
          <form onSubmit={submit} className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#4B5563]">Full Name</span>
              <input className="input-base mt-1" value={fullName} onChange={e => setFullName(e.target.value)} required data-testid="signup-full-name" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#4B5563]">Email</span>
              <input type="email" inputMode="email" autoComplete="email" className="input-base mt-1" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="signup-email" />
              <span className="text-[11px] text-[#4B5563] mt-1 block">You'll use this to log in.</span>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#4B5563]">Phone Number</span>
              <input type="tel" inputMode="tel" autoComplete="tel" className="input-base mt-1" value={phone} onChange={e => setPhone(e.target.value)} placeholder="(803) 555-1234" required data-testid="signup-phone" />
            </label>

            <div>
              <p className="text-xs uppercase tracking-widest font-bold text-[#4B5563] mb-1">Birthday</p>
              <div className="grid grid-cols-2 gap-2">
                <select className="input-base" value={bMonth} onChange={e => { setBMonth(e.target.value); if (bDay && parseInt(bDay,10) > daysInMonth(parseInt(e.target.value,10))) setBDay(""); }} required data-testid="signup-birth-month">
                  <option value="" disabled>Month</option>
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
                <select className="input-base" value={bDay} onChange={e => setBDay(e.target.value)} required data-testid="signup-birth-day">
                  <option value="" disabled>Day</option>
                  {Array.from({ length: dayCount }, (_, i) => i + 1).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <span className="text-[11px] text-[#4B5563] mt-1 block">We'll drop 500 pts in your account every birthday week 🎂</span>
            </div>

            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#4B5563]">Password</span>
              <input type="password" className="input-base mt-1" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} data-testid="signup-password" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest font-bold text-[#4B5563]">Referral Code <span className="text-[#9CA3AF] normal-case tracking-normal">(optional)</span></span>
              <input className="input-base mt-1 uppercase tracking-widest" value={referralCode} onChange={e => setReferralCode(e.target.value.toUpperCase())} placeholder="FRIEND1234" data-testid="signup-referral-code" />
              <span className="text-[11px] text-[#4B5563] mt-1 block">Got a code from a friend? They'll earn 50 points.</span>
            </label>
            <button type="submit" disabled={submitting} className="btn-pink w-full disabled:opacity-50" data-testid="signup-submit-btn">
              {submitting ? "CREATING..." : "CREATE ACCOUNT"}
            </button>
            <p className="text-center text-sm text-[#4B5563]">
              Already have an account?{" "}
              <Link to={`/login${redirect !== "/" ? `?redirect=${encodeURIComponent(redirect)}` : ""}`} className="text-[#FF1F87] font-bold" data-testid="signup-login-link">Log in</Link>
            </p>
          </form>
        </div>
      </section>
    </Layout>
  );
}
